import { useQueryClient } from '@tanstack/react-query';
import Avo from 'analytics/Avo';
import type { Order } from 'api/types/order.types';
import { INTEGRATION_PARTNERS } from 'config/types';
import { useConfig } from 'context/app-config';
import useLocalStorage from 'hooks/useLocalStorage';
import { useQuery } from 'hooks/useQuery';
import useOrderStep from 'packages/shared/newOrder/hooks/useOrderStep';
import {
  selectCurrentOrder,
  selectIsMultipleServicesSelected,
  selectNewOrderData,
  selectNewOrderDataWithAddon,
  selectSelectedServices,
} from 'pages/newOrder/newOrder.selectors';
import { clearState } from 'pages/newOrder/newOrder.slice';
import { usePlaceOrderCore } from 'pages/queries/order.queries';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { RootState } from 'redux-stores/reducers/rootReducer';
import { hasApplePayPayment, selectCustomer, selectPartner } from 'redux-stores/slices/authSlice';
import type { AppDispatch } from 'redux-stores/store';
import { performApplePayPayment } from 'wrappers/apple_pay';
import { notifyError, notifyErrorObject } from 'wrappers/reporting';

import type { PaymentCardRef } from '../types';
import useHandleCareemPayment from './useHandleCareemPayment';

enum PaymentTypesEnum {
  CAREEM_PAY = 'CAREEM_PAY',
  APPLE_PAY = 'APPLE_PAY',
  CREDIT_CARD = 'CREDIT_CARD',
}

export function usePlaceOrder(paymentMethodRef?: React.RefObject<PaymentCardRef>) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const customer = useSelector(selectCustomer);
  const config = useConfig();
  const query = useQuery();
  const selectedServiceLines = useSelector(selectSelectedServices);
  const queryClient = useQueryClient();
  const orderData = useSelector((state: RootState) =>
    config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs
      ? selectNewOrderDataWithAddon(state, !!config?.disablePayment, !!config?.shouldShowPaymentSheetIfMissing)
      : selectNewOrderData(state, !!config?.disablePayment, !!config?.shouldShowPaymentSheetIfMissing)
  );
  const { mutateAsync: placeNewOrder } = usePlaceOrderCore();
  const partner = useSelector(selectPartner);
  const isApplePay = useSelector(hasApplePayPayment);
  const navigate = useNavigate();
  const loaderRef = useRef<any>(null);
  const [showLoader, setShowLoader] = React.useState(false);
  const { activeStep, resetStep, isDelayUponNavigate } = useOrderStep();
  const [persistedOrderData, persistOrderData] = useLocalStorage<Order.NewOrder>('orderData');
  const invoiceId = query.get('invoiceId');
  const { creditCardId, pickupTimeslot } = useSelector(selectCurrentOrder);

  const hasSupportForMultipleDropoffs = useMemo(() => config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, [config]);

  const handlePlaceOrder = React.useCallback(
    async (careemInitialRecurringInvoiceId?: string) => {
      /**
       * If payment is disabled, we should show the payment sheet if the user doesn't have a credit card
       * and the user is not using Apple Pay
       * only when config?.shouldShowPaymentSheetIfMissing is true
       * */
      if (config?.shouldShowPaymentSheetIfMissing && !creditCardId && !isApplePay) {
        paymentMethodRef?.current?.openNoPaymentMethodSheet();
        return;
      }

      let newOrderRequest = orderData ?? persistedOrderData;
      if (!newOrderRequest) return;

      if (partner && partner !== INTEGRATION_PARTNERS.APP) {
        newOrderRequest = {
          ...newOrderRequest,
          customOriginApp: partner,
        };
      }
      if (careemInitialRecurringInvoiceId) {
        newOrderRequest = {
          ...newOrderRequest,
          careemInitialRecurringInvoiceId,
        };
        // check if Apple pay is supported and is also the last payment method
      } else if (isApplePay) {
        try {
          const result = await performApplePayPayment();
          newOrderRequest = {
            ...newOrderRequest,
            applePayPayload: result,
          };
        } catch (e) {
          // apple pay verification failed
          notifyError(t('newOrderPage.applePayFailed'));
          return;
        }
      }

      setShowLoader(true);
      // set loader to show loading state
      loaderRef.current?.triggerLoading();

      placeNewOrder(newOrderRequest)
        .then((resultAction) => {
          const {
            id: orderId,
            isPickupAutoSelected = false,
            pickupTimeslot: { durationText: assignedPickupTime, slotText: pickUpSlotText, date: pickupDay },
            dropoffTimeslot: { slotText: dropoffTimeslotText, date: dropoffDay },
            type,
          } = resultAction.newOrder;

          const paymentMethod = () => {
            if (careemInitialRecurringInvoiceId) {
              return PaymentTypesEnum.CAREEM_PAY;
            }

            if (newOrderRequest.applePayPayload) {
              return PaymentTypesEnum.APPLE_PAY;
            }
            return PaymentTypesEnum.CREDIT_CARD;
          };

          const eventParams = {
            addressGeofence: '',
            orderId,
            orderType: type,
            pickupTime: pickUpSlotText,
            dropoffTime: dropoffTimeslotText,
            pickupDay: pickupDay ?? '',
            dropoffDay: dropoffDay ?? '',
            customerId: undefined,
            origin: undefined,
            paymentMethod: paymentMethod(),
            serviceLinesSelected: hasSupportForMultipleDropoffs ? selectedServiceLines : ['laundry'],
          };

          Avo.orderPlaced(eventParams);

          loaderRef.current?.triggerSuccess(orderId);

          if (isPickupAutoSelected) {
            const oldPickupTime =
              // 60 minutes timeslot
              pickupTimeslot?.slotTextAppendedBetween ??
              // 90/120 minutes timeslot (toLowercase because message starts with caps)
              pickupTimeslot?.pickupConfirmationTime?.toLowerCase();
            toast.dark(
              t('common.changedPickupTimeMessage', {
                oldPickupTime,
                assignedPickupTime,
              })
            );
          }

          if (customer?.completedOrdersCount === 0 && customer?.activeOrdersCount === 0) {
            // TODO: move to backend
            Avo.firstOrderPlaced();
          }
        })
        .catch((error) => {
          loaderRef.current?.triggerFailure();
          // Extract error details from the response
          const errorMessage = error?.response?.data?.infoCode?.message ?? 'unknownError';
          const errorCode = error?.response?.data?.infoCode?.code;

          // Show error message in a toast with fallback if translation is missing
          toast.dark(t(`newOrderPage.${errorMessage}`, `${errorMessage}`));

          if ([404.14, 400.2, 400.19, 400.1].includes(errorCode ?? 0)) {
            // handle unavailable time slot
            resetStep();
            navigate(`/newOrder`);
            toast.dark(t(`newOrderPage.updateTimeslot`, 'Please select a new timeslot.'));
          }
        });

      Avo.orderSubmit();
    },
    [
      config?.shouldShowPaymentSheetIfMissing,
      creditCardId,
      isApplePay,
      orderData,
      persistedOrderData,
      partner,
      placeNewOrder,
      paymentMethodRef,
      t,
      queryClient,
      hasSupportForMultipleDropoffs,
      selectedServiceLines,
      customer?.completedOrdersCount,
      customer?.activeOrdersCount,
      pickupTimeslot?.slotTextAppendedBetween,
      pickupTimeslot?.pickupConfirmationTime,
      resetStep,
      navigate,
    ]
  );

  const { handlePayment, isNewPaymentModuleAvailable } = useHandleCareemPayment({ handlePlaceOrder });

  const handlePlaceOrderClick = React.useCallback(async () => {
    const initiatePayment = config?.api?.initiatePayment;
    if (!initiatePayment) {
      await handlePlaceOrder();
    } else {
      try {
        const { redirectURI } = await initiatePayment({
          customOriginApp: partner,
          step: activeStep,
        });

        // if new payment module is not available, persist order data
        if (!isNewPaymentModuleAvailable) {
          persistOrderData(orderData);
        }

        handlePayment({
          redirectURI,
        });
      } catch (e) {
        toast.dark(t('common.initiatePaymentFailed'));
        notifyErrorObject(e);
      }
    }
  }, [activeStep, config?.api?.initiatePayment, handlePlaceOrder, orderData, partner, persistOrderData, t]);

  const hasMultipleServicesSelected = useSelector(selectIsMultipleServicesSelected);

  const handleLoaderComplete = React.useCallback(
    async (state: 'success' | 'failure', id: string) => {
      setShowLoader(false);
      if (state === 'success') {
        await dispatch(clearState());
        resetStep();

        // if new payment module is not available, remove the persisted order data
        if (!isNewPaymentModuleAvailable) {
          persistOrderData(undefined);
        }

        // as a workaround first we go back to newOrder
        // then we navigate to the order details page
        // avoiding this approach for Careem, InstaShop
        setTimeout(
          () => {
            if (hasMultipleServicesSelected) {
              navigate(`/orders`, { replace: true });
            } else {
              navigate(`/order/details/${id}`, { replace: true });
            }
          },
          isDelayUponNavigate ? 400 : 0
        );
      }
    },
    [dispatch, resetStep, persistOrderData, hasMultipleServicesSelected, navigate]
  );
  useEffect(() => {
    if (!invoiceId) {
      return;
    }

    async function submitOrder() {
      await handlePlaceOrder(invoiceId ?? undefined);
    }

    submitOrder();
  }, [invoiceId]);

  const [showPricingSheet, setShowPricingSheet] = React.useState(false);
  const togglePricingSheet = React.useCallback((value: boolean) => setShowPricingSheet(value), []);

  const handleClosePricingSheet = () => {
    togglePricingSheet(false);
  };
  return {
    orderData,
    loaderRef,
    showLoader,
    handlePlaceOrderClick,
    handleLoaderComplete,
    showPricingSheet,
    togglePricingSheet,
    handleClosePricingSheet,
  };
}
